import { Component, OnInit, ChangeDetectionStrategy, inject, DestroyRef, ChangeDetectorRef } from '@angular/core';
import { Tabs, TabList, Tab } from '@openng/optimus-ui/tabs';
import { Message } from '@openng/optimus-ui/message';
import { Checkbox } from '@openng/optimus-ui/checkbox';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoginService } from '../../core/services/login.service';
import { CompaniesService } from '../common/services/companies.service';
import { AuthenticationProcessesService } from '../common/services/authentication-processes.service';
import { AuthServicesService, AuthServiceConfig } from './services/auth-services.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from, switchMap } from 'rxjs';
import { TwoFactorAuthService } from '../../core/services/two-factor-auth.service';
import { ForgotPasswordService } from './services/forgot-password.service';

// PrimeNG Imports
import { ButtonModule } from '@openng/optimus-ui/button';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { PasswordModule } from '@openng/optimus-ui/password';
import { DialogModule } from '@openng/optimus-ui/dialog';

type AuthMethod = 'password' | 'passkey' | 'external' | 'authenticator' | 'yubico';

interface AuthenticationProcess {
    id: number;
    Name: string;
    IsEnabled: boolean;
    Steps: AuthenticationProcessStep[];
}

interface AuthenticationProcessStep {
    id: number;
    Order: number;
    UserPassword: boolean | null;
    OAuth: boolean | null;
    Authenticator: boolean | null;
    YubicoOTP: boolean | null;
    WebAuthn: boolean | null;
}

@Component({
    selector: 'app-auth-init',
    standalone: true,
    imports: [
        Message,
        FormsModule,
        Checkbox,
        NgClass,
        Tabs,
        TabList,
        Tab,
        TranslateModule,
        ButtonModule,
        InputTextModule,
        PasswordModule,
        DialogModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
@if (showNewLogin) {
<div class="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-(--stela-surface)">
    <!-- Brand panel (Stela 2 prototype) -->
    <div class="hidden lg:flex relative overflow-hidden flex-col justify-between p-12 text-white bg-[linear-gradient(160deg,#3E2C42_0%,#1F1422_100%)]">
        <div class="absolute inset-0 opacity-[0.08] pointer-events-none bg-[radial-gradient(circle_at_20%_30%,#D4956A_0%,transparent_40%),radial-gradient(circle_at_80%_70%,#D4956A_0%,transparent_40%)]"></div>
        <div class="relative flex items-center gap-4">
            <img src="/assets/img/Stela_logo_horizontal.png" class="h-14 w-auto max-w-full block" alt="Stela · Protocolo & Eventos">
        </div>
        <div class="relative max-w-[480px]">
            <h2 class="m-0 text-[44px] leading-[1.15] font-medium" style="font-family: var(--stela-font-display)">{{ 'login.brand_headline' | translate }}</h2>
            <p class="mt-[18px] text-base leading-relaxed opacity-75">{{ 'login.brand_tagline' | translate }}</p>
        </div>
        <div class="relative text-[11px] opacity-50 tracking-[0.8px] uppercase">&copy; Diaple Networking SL {{ currentYear }}</div>
    </div>

    <!-- Form pane -->
    <div class="flex items-center justify-center p-6 sm:p-12">
        <div class="w-full max-w-[380px]">
            <!-- Company Logo -->
            @if (showCompanyData && companyLogoUrl) {
                <div class="flex justify-center mb-8">
                    <img [src]="companyLogoUrl" alt="Logo" title="Logo" class="max-w-full max-h-16 object-contain"/>
                </div>
            }

            @if (showBackLink && !showProcessSelector && currentStep) {
                <button type="button"
                        (click)="resetProcess()"
                        class="inline-flex items-center gap-1.5 mb-3.5 p-0 bg-transparent border-0 text-[13px] text-gray-400 hover:text-(--stela-gold) cursor-pointer transition-colors">
                    <span>&larr;</span>
                    <span>{{ 'login.back_to_start' | translate }}</span>
                </button>
            }

            <h1 class="m-0 text-[32px] text-[#1A2744]" style="font-family: var(--stela-font-display)">
                {{ (isCodeOnlyStep ? 'login.verification_title' : 'login.welcome') | translate }}
            </h1>
            @if (showProcessSelector) {
                <p class="mt-1.5 text-sm text-gray-500">{{ 'login.select_auth_process' | translate }}</p>
            } @else if (availableMethods.length > 1) {
                <p class="mt-1.5 text-sm text-gray-500">{{ 'login.choose_method' | translate }}</p>
            } @else if (currentStep?.UserPassword) {
                <p class="mt-1.5 text-sm text-gray-500">{{ 'login.description' | translate }}</p>
            }

            <!-- Process Selector -->
            @if (showProcessSelector) {
                <div class="flex flex-col gap-2.5 mt-7">
                    @for (process of processes; track process.id) {
                        <button type="button"
                                (click)="selectProcess(process.id)"
                                class="w-full flex justify-between items-center text-left px-[18px] py-3.5 bg-(--stela-surface) hover:bg-[#FEFCF6] border border-gray-200 hover:border-gold-dark rounded-lg text-sm text-gray-800 cursor-pointer transition-colors">
                            <span>{{ process.Name }}</span>
                            <span class="text-gray-400 text-lg">&rsaquo;</span>
                        </button>
                    }
                </div>
            }

            <!-- Authentication Form -->
            @if (!showProcessSelector && currentStep) {
                <!-- Method tabs: a step that allows several methods offers them one at a time -->
                @if (availableMethods.length > 1) {
                    <p-tabs class="mt-6" [value]="activeMethod" (valueChange)="selectMethod($any($event))">
                        <p-tablist>
                            @for (method of availableMethods; track method) {
                                <p-tab [value]="method" class="flex-1 justify-center">{{ methodLabelKeys[method] | translate }}</p-tab>
                            }
                        </p-tablist>
                    </p-tabs>
                }
                <form class="space-y-6" [ngClass]="availableMethods.length > 1 ? 'mt-6' : 'mt-7'">
                    <!-- User Password Step -->
                    @if (currentStep.UserPassword && showsMethod('password')) {
                        <div>
                            <div class="flex flex-col gap-1.5">
                                <label for="username" class="text-[13px] font-medium text-gray-700">{{ 'login.username' | translate }}</label>
                                <input
                                    id="username"
                                    type="text"
                                    autocomplete="username"
                                    class="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-(--stela-surface) focus:border-gold-dark focus:ring-2 focus:ring-[#B0923E]/20 outline-hidden transition-colors"
                                    [(ngModel)]="loginData.username"
                                    name="username"/>
                            </div>
                            <div class="flex flex-col gap-1.5 mt-3.5">
                                <label for="password" class="text-[13px] font-medium text-gray-700">{{ 'login.password' | translate }}</label>
                                <div class="relative flex items-center h-10 border border-gray-200 rounded-md bg-(--stela-surface) focus-within:border-gold-dark focus-within:ring-2 focus-within:ring-[#B0923E]/20 transition-colors overflow-hidden">
                                    <input
                                        id="password"
                                        [type]="passwordType"
                                        autocomplete="current-password"
                                        class="flex-1 h-full px-3 text-sm border-none outline-hidden bg-transparent"
                                        [(ngModel)]="loginData.password"
                                        name="password"
                                        (keyup.enter)="checkUserPassword()"/>
                                    <button type="button"
                                            class="px-3 h-full bg-transparent border-0 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors flex items-center"
                                            [attr.aria-label]="'login.password' | translate"
                                            (click)="togglePasswordVisibility()">
                                        <i class="fa" [ngClass]="passwordIcon"></i>
                                    </button>
                                </div>
                            </div>
                            <div class="flex justify-between items-center gap-4 mt-3.5 text-[13px]">
                                <div class="flex items-center gap-1.5 text-gray-700">
                                    <p-checkbox [binary]="true" inputId="remember" [(ngModel)]="loginData.remember" name="remember" />
                                    <label for="remember" class="cursor-pointer">{{ 'login.remember' | translate }}</label>
                                </div>
                                <a class="text-gold-dark hover:underline cursor-pointer whitespace-nowrap"
                                   (click)="openForgotPasswordModal()">
                                    {{ 'login.forgot_psw' | translate }}
                                </a>
                            </div>
                            <p-button type="button"
                                    styleClass="w-full h-11 mt-6 text-sm font-semibold"
                                    [label]="'login.button_access' | translate"
                                    [loading]="isLoading"
                                    (onClick)="checkUserPassword()" />
                        </div>
                    }


                    <!-- WebAuthn Step -->
                    @if (currentStep.WebAuthn && webAuthnAvailable && showsMethod('passkey')) {
                        <div class="flex flex-col items-center gap-4">
                            <p-button type="button"
                                    styleClass="w-full gap-2.5 px-6 py-3.5 bg-[#0F172A]! hover:bg-[#1E293B]! border-0! text-white! text-[15px] font-medium rounded-lg"
                                    [label]="'login.button_webauthn' | translate"
                                    [icon]="isLoading ? undefined : 'fa fa-fingerprint'"
                                    [loading]="isLoading"
                                    (onClick)="checkWebauthn()" />
                        </div>
                    }

                    <!-- OAuth Step -->
                    @if (currentStep.OAuth && authServices.length > 0 && showsMethod('external')) {
                        <div class="space-y-2.5">
                            @for (service of authServices; track service.id) {
                                <button
                                    class="w-full flex items-center justify-center gap-2.5 px-[18px] py-3 text-white text-sm font-medium rounded-md border-0 cursor-pointer hover:opacity-90 transition-opacity"
                                    type="button"
                                    [style.background-color]="service.color || '#444'"
                                    (click)="loginExternal(service)">
                                    @if (service.imageUid && service.imageUrl) {
                                        <img [src]="service.imageUrl" class="w-5 h-5 object-contain" [alt]="service.name"/>
                                    }
                                    <span>Login con {{ service.name }}</span>
                                </button>
                            }
                        </div>
                    }

                    <!-- Authenticator Step -->
                    @if (currentStep.Authenticator && showsMethod('authenticator')) {
                        <div>
                            <div class="flex justify-center text-gray-400">
                                <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                    <path d="M12 3 L20 6 V12 c0 5 -3.5 8.5 -8 9 c-4.5 -0.5 -8 -4 -8 -9 V6 Z"/>
                                </svg>
                            </div>
                            <label for="authenticator_code" class="block text-center text-[15px] font-semibold text-gray-800 mt-1">{{ 'login.authenticator_code' | translate }}</label>
                            <p class="text-center text-[13px] text-gray-500 mt-1.5 leading-snug">{{ 'login.authenticator_hint' | translate }}</p>
                            <input
                                id="authenticator_code"
                                type="text"
                                inputmode="numeric"
                                autocomplete="one-time-code"
                                class="w-full h-[52px] mt-5 px-3 text-[22px] font-medium text-center tracking-[0.6em] text-gray-800 border border-gray-200 rounded-md bg-(--stela-surface) focus:border-gold-dark outline-hidden transition-colors"
                                [(ngModel)]="loginData.authenticator_code"
                                name="authenticator_code"
                                maxlength="6"
                                (ngModelChange)="onAuthenticatorCodeChange($event)"
                                (keyup.enter)="validateAuthenticator()"/>
                            <button type="button"
                                    class="w-full h-11 mt-6 bg-(--stela-gold) hover:bg-(--stela-gold-dark) text-(--stela-aubergine-dark,#2A1E2E) text-sm font-semibold rounded-md border-0 cursor-pointer transition-colors disabled:opacity-55 disabled:cursor-not-allowed"
                                    [disabled]="loginData.authenticator_code.length !== 6"
                                    (click)="validateAuthenticator()">
                                {{ 'login.verify' | translate }}
                            </button>
                        </div>
                    }


                    <!-- Yubico OTP Step -->
                    @if (currentStep.YubicoOTP && showsMethod('yubico')) {
                        <div class="flex flex-col gap-1.5">
                            <label for="yubico_otp" class="text-[13px] font-medium text-gray-700">Yubico OTP</label>
                            <input
                                id="yubico_otp"
                                type="text"
                                class="w-full h-10 px-3 text-sm text-center tracking-wider font-mono border border-gray-200 rounded-md bg-(--stela-surface) focus:border-gold-dark focus:ring-2 focus:ring-[#B0923E]/20 outline-hidden transition-colors"
                                [placeholder]="'general.code' | translate"
                                [(ngModel)]="loginData.yubico_otp"
                                name="yubico_otp"
                                maxlength="44"
                                (ngModelChange)="onYubicoOTPChange($event)"
                                (keyup.enter)="validateYubicoOTP()"/>
                            <button type="button"
                                    class="w-full h-11 mt-4 bg-(--stela-gold) hover:bg-(--stela-gold-dark) text-(--stela-aubergine-dark,#2A1E2E) text-sm font-semibold rounded-md border-0 cursor-pointer transition-colors"
                                    (click)="validateYubicoOTP()">
                                {{ 'login.verify' | translate }}
                            </button>
                        </div>
                    }

                    @if (errorMessage) {
                        <p-message size="small" severity="error" icon="pi pi-exclamation-circle" styleClass="w-full">{{ errorMessage }}</p-message>
                    }
                </form>
            }

            <!-- Footer (mobile only: the brand panel carries it on desktop) -->
            <p class="lg:hidden mt-8 text-center text-gray-400 text-xs">
                &copy; Diaple Networking SL {{ currentYear }}
            </p>
        </div>
    </div>
</div>
}

<p-dialog [(visible)]="showForgotPasswordModal" [modal]="true" [dismissableMask]="true" [style]="{ width: '28rem' }" [header]="'login.forgot_psw' | translate">
    @if (!forgotPasswordSent) {
        <p class="text-sm text-gray-600 mb-4">{{ 'login.forgot_password_description' | translate }}</p>
        <input
            type="text"
            class="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#B0923E]/20 focus:border-gold-dark outline-hidden transition-all placeholder:text-gray-400"
            [placeholder]="'login.mail' | translate"
            [(ngModel)]="forgotPasswordEmail"
            name="forgotPasswordEmail"
            (keyup.enter)="submitForgotPassword()"/>
        <div class="flex justify-end gap-2 mt-5">
            <button type="button"
               class="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-md transition-all"
               (click)="closeForgotPasswordModal()">
                {{ 'login.cancel' | translate }}
            </button>
            <p-button type="button"
               styleClass="px-5 py-2.5 font-semibold"
               [label]="'login.send' | translate"
               [loading]="forgotPasswordSubmitting"
               [disabled]="!forgotPasswordEmail || forgotPasswordSubmitting"
               (onClick)="submitForgotPassword()" />
        </div>
    } @else {
        <p class="text-sm text-gray-700 mb-5">{{ 'login.forgot_password_sent' | translate }}</p>
        <div class="flex justify-end">
            <button type="button"
               class="px-5 py-2.5 bg-(--stela-gold) hover:bg-(--stela-gold-dark) text-(--stela-aubergine-dark) font-semibold rounded-md transition-all"
               (click)="closeForgotPasswordModal()">
                {{ 'login.cancel' | translate }}
            </button>
        </div>
    }
</p-dialog>
    `,
    styles: [] // Styles now handled by Tailwind utility classes
})
export class AuthInitComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly translate = inject(TranslateService);

    // Display flags
    showNewLogin = true;
    showCompanyData = false;
    showProcessSelector = false;
    showBackLink = false;

    // Data
    loginData = {
        username: '',
        password: '',
        remember: false,
        authenticator_code: '',
        yubico_otp: ''
    };

    // Password visibility toggle
    passwordType: 'password' | 'text' = 'password';
    passwordIcon = 'fa-eye';

    // Forgot-password modal
    showForgotPasswordModal = false;
    forgotPasswordEmail = '';
    forgotPasswordSubmitting = false;
    forgotPasswordSent = false;

    private readonly forgotPasswordService = inject(ForgotPasswordService);

    openForgotPasswordModal(): void {
        this.forgotPasswordEmail = '';
        this.forgotPasswordSent = false;
        this.showForgotPasswordModal = true;
    }

    closeForgotPasswordModal(): void {
        this.showForgotPasswordModal = false;
    }

    submitForgotPassword(): void {
        if (!this.forgotPasswordEmail || this.forgotPasswordSubmitting) {
            return;
        }
        this.forgotPasswordSubmitting = true;
        this.forgotPasswordService.forgotPassword(this.forgotPasswordEmail).subscribe({
            next: () => {
                this.forgotPasswordSubmitting = false;
                this.forgotPasswordSent = true;
                this.cdRef.markForCheck();
            },
            error: () => {
                // Misma respuesta que el éxito a propósito: el backend ya responde siempre
                // igual (ver ForgotPassword en AuthController), así que un error de red aquí
                // es la única situación en la que el usuario podría notar la diferencia — y
                // aun así no hay nada útil que decirle sobre si su cuenta existe.
                this.forgotPasswordSubmitting = false;
                this.forgotPasswordSent = true;
                this.cdRef.markForCheck();
            }
        });
    }

    // Services
    private companiesService = inject(CompaniesService);
    private authProcessesService = inject(AuthenticationProcessesService);
    private authServicesService = inject(AuthServicesService);
    private readonly twoFactorAuthService = inject(TwoFactorAuthService);

    // State
    isLoading = false;
    errorMessage: string | null = null;
    authServices: AuthServiceConfig[] = [];
    processes: AuthenticationProcess[] = [];
    currentStep: AuthenticationProcessStep | null = null;
    webAuthnAvailable = this.twoFactorAuthService.hasRegisteredPasskey();
    currentYear = new Date().getFullYear();

    /** Methods the current step offers, in tab order. More than one → the form shows tabs. */
    get availableMethods(): AuthMethod[] {
        const step = this.currentStep;
        if (!step) {
            return [];
        }
        const methods: AuthMethod[] = [];
        if (step.UserPassword) methods.push('password');
        if (step.WebAuthn && this.webAuthnAvailable) methods.push('passkey');
        if (step.OAuth && this.authServices.length > 0) methods.push('external');
        if (step.Authenticator) methods.push('authenticator');
        if (step.YubicoOTP) methods.push('yubico');
        return methods;
    }

    readonly methodLabelKeys: Record<AuthMethod, string> = {
        password: 'login.password',
        passkey: 'login.tab_passkey',
        external: 'login.tab_external',
        authenticator: 'login.tab_authenticator',
        yubico: 'login.tab_yubico'
    };

    /** Tab picked by the user; null means "the first one available". */
    private selectedMethod: AuthMethod | null = null;

    get activeMethod(): AuthMethod | null {
        const methods = this.availableMethods;
        return this.selectedMethod && methods.includes(this.selectedMethod) ? this.selectedMethod : methods[0] ?? null;
    }

    selectMethod(method: AuthMethod): void {
        this.selectedMethod = method;
        this.errorMessage = null;
        this.cdRef.markForCheck();
    }

    showsMethod(method: AuthMethod): boolean {
        return this.availableMethods.length <= 1 || this.activeMethod === method;
    }

    /** Second-factor step (code only, no password): the heading switches to "Verificación" as in the prototype. */
    get isCodeOnlyStep(): boolean {
        const step = this.currentStep;
        return !this.showProcessSelector && !!step && !step.UserPassword && !!(step.Authenticator || step.YubicoOTP);
    }
    companyLogoUrl: string | null = null;

    constructor(
        public loginService: LoginService,
        public router: Router,
        public route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        // Load translations - use saved language or default to Spanish
        const savedLang = localStorage.getItem('app-language') || 'es';
        this.translate.use(savedLang).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.cdRef.markForCheck();
        });

        // Check remembered user
        const rememberedUser = localStorage.getItem('user');
        if (rememberedUser && localStorage.getItem('remember')) {
            this.loginData.username = rememberedUser;
            this.loginData.remember = true;
        }

        // Load company logo if available
        this.loadCompanyLogo();

        // Check for process ID in route (e.g., after OAuth callback)
        const processId = this.route.snapshot.params['processId'];

        if (processId) {
            // Resume an existing authentication process
            this.loadNextStep(Number.parseInt(processId, 10));
        } else {
            // Start fresh: load enabled authentication processes
            this.loadEnabledProcesses();
        }
    }

    private loadCompanyLogo(): void {
        // Load company logo using legacy pattern
        // Companies/Count returns company_id if single-tenant, or 0 if multi-tenant
        this.companiesService.getCount()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (companyId) => {
                    if (companyId !== 0) {
                        this.companyLogoUrl = this.companiesService.getLogoUrl(companyId);
                        this.showCompanyData = true;
                        this.cdRef.markForCheck();
                    }
                },
                error: () => {}
            });
    }

    private loadEnabledProcesses(): void {
        this.authProcessesService.getEnabled()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (processes: any) => {
                    this.processes = processes || [];
                    if (this.processes.length === 1) {
                        // Single process: auto-select and load its first step
                        this.selectProcess(this.processes[0].id);
                    } else if (this.processes.length > 1) {
                        // Multiple processes: show process selector
                        this.showProcessSelector = true;
                        this.cdRef.markForCheck();
                    } else {
                        // No processes configured: create a default step with UserPassword
                        this.currentStep = {
                            id: 0,
                            Order: 1,
                            UserPassword: true,
                            OAuth: null,
                            Authenticator: null,
                            YubicoOTP: null,
                            WebAuthn: null
                        };
                        this.showProcessSelector = false;
                        this.cdRef.markForCheck();
                    }
                },
                error: () => {
                    // If we can't load processes, create a default step with UserPassword
                    this.currentStep = {
                        id: 0,
                        Order: 1,
                        UserPassword: true,
                        OAuth: null,
                        Authenticator: null,
                        YubicoOTP: null,
                        WebAuthn: null
                    };
                    this.showProcessSelector = false;
                    this.cdRef.markForCheck();
                }
            });
    }

    selectProcess(processId: number): void {
        this.showProcessSelector = false;
        // Only worth offering a way back when there is a selector to go back to: with a single
        // process the component auto-selects it and the link would dead-end.
        this.showBackLink = this.processes.length > 1;
        this.loadNextStep(processId);
    }

    /**
     * Aborts the authentication process and returns to the process selector.
     *
     * The server drops the process cookie; the local state is reset regardless of how that call
     * goes, so a network failure never leaves the user trapped in the step they picked.
     */
    resetProcess(): void {
        this.loginService.resetAuthProcess()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => this.backToSelector(),
                error: () => this.backToSelector()
            });
    }

    private backToSelector(): void {
        this.showProcessSelector = true;
        this.showBackLink = false;
        this.currentStep = null;
        this.selectedMethod = null;
        this.errorMessage = null;
        this.isLoading = false;
        this.authServices = [];
        this.loginData.password = '';
        this.loginData.authenticator_code = '';
        this.loginData.yubico_otp = '';
        this.cdRef.markForCheck();
    }

    private loadNextStep(processId?: number): void {
        this.loginService.nextStep(processId ?? null)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response: any) => {
                    // Check if all steps completed (empty response, null, or completed status)
                    if (response == null || response === '' || Object.keys(response).length === 0 || response.completed === true) {
                        // All steps completed - get auth info
                        this.getAuthInfo();
                        return;
                    }
                    this.currentStep = response;
                    this.applyStep(response);
                },
                error: (err) => {
                    // HTTP 202 or 204 might come as error with empty body - treat as completed
                    if (err.status === 202 || err.status === 204) {
                        this.getAuthInfo();
                        return;
                    }
                    // Create a default step with UserPassword
                    this.currentStep = {
                        id: 0,
                        Order: 1,
                        UserPassword: true,
                        OAuth: null,
                        Authenticator: null,
                        YubicoOTP: null,
                        WebAuthn: null
                    };
                    this.cdRef.markForCheck();
                }
            });
    }

    private applyStep(stepData: AuthenticationProcessStep): void {
        // A new step starts on its first method, not on the tab picked in the previous one.
        this.selectedMethod = null;

        // Load OAuth services if this step allows OAuth
        if (stepData.OAuth) {
            this.loadAuthServices();
        }

        this.cdRef.markForCheck();
    }

    private loadAuthServices(): void {
        this.authServicesService.getAuthServices()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (services) => {
                    // The external tab appears only now; pin the tab the user already had so
                    // a slow provider list doesn't switch the form out from under them.
                    const shownMethod = this.activeMethod;
                    if (!this.selectedMethod && shownMethod) {
                        this.selectedMethod = shownMethod;
                    }
                    this.authServices = (services || [])
                        .filter(service => service.webapp_enabled === true)
                        .map(service => {
                            // Build image URL if imageUid exists
                            if (service.imageUid) {
                                service.imageUrl = this.authServicesService.getImageUrl(service.imageUid);
                            }
                            return service;
                        });
                    this.cdRef.markForCheck();
                },
                error: () => {}
            });
    }

    togglePasswordVisibility(): void {
        this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
        this.passwordIcon = this.passwordType === 'password' ? 'fa-eye' : 'fa-eye-slash';
        this.cdRef.markForCheck();
    }

    checkUserPassword(): void {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.errorMessage = null;

        this.loginService.validateUser(this.loginData.username, this.loginData.password)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
            next: (response) => {
                this.isLoading = false;
                if (response.success) {
                    if (this.loginData.remember) {
                        localStorage.setItem('remember', 'true');
                        localStorage.setItem('user', this.loginData.username);
                    } else {
                        // Opting out must forget a previously remembered user. 'user' itself stays
                        // for this session (saveTokenInfo rewrites it) and logout drops it once
                        // 'remember' is gone.
                        localStorage.removeItem('remember');
                    }
                    // Move to next step
                    this.loadNextStep();
                } else {
                    this.errorMessage = this.translate.instant('login.invalid_credentials') || 'Credenciales incorrectas';
                }
                this.cdRef.markForCheck();
            },
            error: () => {
                this.isLoading = false;
                this.errorMessage = this.translate.instant('login.invalid_credentials') || 'Credenciales incorrectas';
                this.cdRef.markForCheck();
            }
        });
    }

    checkWebauthn(): void {
        this.isLoading = true;
        this.errorMessage = null;
        this.cdRef.markForCheck();

        this.twoFactorAuthService.getAssertionOptions()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(options => from(this.twoFactorAuthService.getAssertion(options))),
                switchMap(assertion => {
                    if (!assertion) throw new Error('No assertion returned');
                    const assertionJson = this.twoFactorAuthService.assertionToJSON(assertion as PublicKeyCredential);
                    return this.twoFactorAuthService.makeAssertion(assertionJson);
                })
            )
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    this.twoFactorAuthService.markPasskeyRegistered();
                    this.loadNextStep();
                },
                error: (err) => {
                    this.isLoading = false;
                    if (err?.name === 'NotAllowedError') {
                        this.errorMessage = this.translate.instant('login.webauthn_cancelled') || 'Authentication cancelled';
                    } else if (err?.name === 'InvalidStateError') {
                        this.errorMessage = this.translate.instant('login.webauthn_no_device') || 'No registered device found';
                    } else {
                        this.errorMessage = this.translate.instant('login.webauthn_failed') || 'Authentication failed, please try again';
                    }
                    this.cdRef.markForCheck();
                }
            });
    }

    onAuthenticatorCodeChange(code: string): void {
        // Auto-submit when 6 digits are entered
        if (code.length === 6) {
            setTimeout(() => {
                this.validateAuthenticator();
            }, 100);
        }
    }

    validateAuthenticator(): void {
        this.errorMessage = null;
        if (this.loginData.authenticator_code.length === 6) {
            this.loginService.validateAuthenticator(this.loginData.authenticator_code)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    if (result.success) {
                        this.loadNextStep();
                    } else {
                        this.errorMessage = this.translate.instant('login.invalid_code') || 'Código incorrecto';
                        this.cdRef.markForCheck();
                    }
                },
                error: () => {
                    this.errorMessage = this.translate.instant('login.error_validating_code') || 'Error al validar el código';
                    this.cdRef.markForCheck();
                }
            });
        }
    }

    onYubicoOTPChange(code: string): void {
        // Auto-submit when 44 characters are entered
        if (code.length === 44) {
            setTimeout(() => {
                this.validateYubicoOTP();
            }, 100);
        }
    }

    validateYubicoOTP(): void {
        this.errorMessage = null;
        if (this.loginData.yubico_otp) {
            this.loginService.validateYubicoOTP(this.loginData.yubico_otp)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    if (result.success) {
                        this.loadNextStep();
                    } else {
                        this.errorMessage = this.translate.instant('login.invalid_code') || 'Código incorrecto';
                        this.cdRef.markForCheck();
                    }
                },
                error: () => {
                    this.errorMessage = this.translate.instant('login.error_validating_code') || 'Error al validar el código';
                    this.cdRef.markForCheck();
                }
            });
        }
    }

    loginExternal(service: AuthServiceConfig): void {
        this.loginService.getOAuthChallenge(service.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    if (response.url) {
                        location.href = response.url;
                    }
                }
            });
    }

    public getAuthInfo(): void {
        this.loginService.getAuthInfo()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    if (response.success && response.info) {
                        this.loginService.saveTokenInfo(response.info, this.loginData, true);

                        // Load company info for inactivity timeout
                        const companyId = response.info?.company_id || response.company?.id;
                        if (companyId) {
                            this.companiesService.getById(companyId)
                                .pipe(takeUntilDestroyed(this.destroyRef))
                                .subscribe({
                                    next: (company) => {
                                        localStorage.setItem('userInactivityTimeout', String(company.user_inactivity_timeout ?? '30'));
                                        this.loginService.resetInactivityTimeoutDate();
                                    },
                                    error: () => {}
                                });
                        }

                        // Navigate to dashboard
                        this.router.navigate(['/dashboard/list']);
                    } else if (response.info) {
                        // Response has info but success might be missing - try anyway
                        this.loginService.saveTokenInfo(response.info, this.loginData, true);
                        this.router.navigate(['/dashboard/list']);
                    } else {
                        console.error('Auth info response missing required data:', response);
                        this.errorMessage = this.translate.instant('login.error_login') || 'Error al iniciar sesión';
                        this.cdRef.markForCheck();
                    }
                },
                error: (err) => {
                    console.error('Error getting auth info:', err);
                    this.errorMessage = err?.statusCode === 403 && err?.code === 'ACCOUNT_PENDING_ACTIVATION'
                        ? this.translate.instant('auth.account_pending_activation')
                        : this.translate.instant('login.error_login') || 'Error al iniciar sesión';
                    this.cdRef.markForCheck();
                }
            });
    }
}
